import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface ProviderDto {
  ID: string;
  PublisherID?: string | null;
  PublishersName?: string | null;
  Name: string;
  Site?: string | null;
  Status?: number | string;
}

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ProvidersApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getProviders(
    params?: { status?: 'active' | 'inactive' | 'all' },
    options?: { skipDcr?: boolean }
  ): Observable<ProviderDto[]> {
    const query = params?.status ? `?status=${params.status}` : '';
    const endpoint = this.composeEndpoint(`/webapp/crud/providers${query}`);
    return this.http.get<GenericResponse<ProviderDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote, options) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[providers-api] list failed', error);
        return throwError(() => error);
      })
    );
  }

  createProvider(body: ProviderDto): Observable<ProviderDto> {
    const endpoint = this.composeEndpoint('/webapp/crud/providers');
    return this.http.post<GenericResponse<ProviderDto>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => this.requirePayload(res)),
      catchError(error => {
        console.error('[providers-api] create failed', error);
        return throwError(() => error);
      })
    );
  }

  updateProvider(body: ProviderDto): Observable<ProviderDto> {
    const endpoint = this.composeEndpoint('/webapp/crud/providers');
    return this.http.put<GenericResponse<ProviderDto>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => this.requirePayload(res)),
      catchError(error => {
        console.error('[providers-api] update failed', error);
        return throwError(() => error);
      })
    );
  }

  private requirePayload<T>(res: GenericResponse<T>): T {
    if (res.payload === undefined || res.payload === null) {
      throw new Error(res.error?.message || 'Empty response payload');
    }
    return res.payload;
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean, options?: { skipDcr?: boolean }): HttpHeaders {
    if (useRemote) {
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const token = this.authService.getTncApiToken();
      if (token) {
        headers = headers.set('Authorization', token);
      }
      return headers;
    }

    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }
}
